import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { ProjectGroup } from "../../types";
import DraftPromptDialog from "../Common/DraftPromptDialog";
import {
  buildDraftKey,
  clearDraft,
  loadDraft,
  saveDraft,
} from "../../utils/draftStorage";

interface Props {
  open: boolean;
  projectId: string;
  projectName: string;
  group?: ProjectGroup | null; 
  onClose: () => void;
  onCreated: () => void;
  onUpdated?: () => void;
}

const ProjectGroupForm: React.FC<Props> = ({
  open,
  projectId,
  projectName,
  group,
  onClose,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditMode = !!group;
  const [draftPromptOpen, setDraftPromptOpen] = useState(false);
  const [draftToRestore, setDraftToRestore] = useState<{ name: string } | null>(
    null,
  );
  const draftPromptShownRef = useRef(false);
  const skipDraftSaveRef = useRef(false);

  const draftKey = buildDraftKey("projectGroup", projectId);

  useEffect(() => {
    if (open) {
      if (group) {
        setName(group.name || "");
      } else {
        setName("");
      }
      setError(null);
    }
  }, [open, group]);

  useEffect(() => {
    if (!open || isEditMode) {
      draftPromptShownRef.current = false;
      setDraftPromptOpen(false);
      setDraftToRestore(null);
      return;
    }
    skipDraftSaveRef.current = false;
    if (draftPromptShownRef.current) return;
    const run = async () => {
      const draft = await loadDraft<typeof draftToRestore>(draftKey);
      if (!draft?.data) return;

      const hasContent = !!draft.data.name;
      if (!hasContent) {
        await clearDraft(draftKey);
        return;
      }

      setDraftToRestore(draft.data);
      setDraftPromptOpen(true);
      draftPromptShownRef.current = true;
    };
    void run();
  }, [open, isEditMode, draftKey]);

  useEffect(() => {
    if (!open || isEditMode) return;
    if (skipDraftSaveRef.current) return;
    if (draftPromptOpen) return;
    const shouldSave = !!name.trim();

    if (!shouldSave) {
      clearDraft(draftKey);
      return;
    }

    saveDraft(draftKey, { name });
  }, [open, isEditMode, name, draftKey, draftPromptOpen]);

  const handleUseDraft = () => {
    if (!draftToRestore) return;
    setName(draftToRestore.name || "");
    setDraftPromptOpen(false);
  };

  const handleStartNew = () => {
    clearDraft(draftKey);
    setName("");
    setDraftPromptOpen(false);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t("projects.detail.requiredFields") || "Название обязательно");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (isEditMode && group) {
        const { updateProjectGroup } = await import("../../services/projectService");
        await updateProjectGroup(group.id, {
          name: name.trim(),
        });
        if (onUpdated) {
          onUpdated();
        }
      } else {
        const { createProjectGroup } = await import("../../services/projectService");
        await createProjectGroup({
          projectId,
          projectName,
          name: name.trim(),
          color: 0,
          taskPlacement: "Bottom",
          type: "Work",
        });
        onCreated();
      }
      setName("");
      if (!isEditMode) {
        clearDraft(draftKey);
      }
      onClose();
    } catch (err: any) {
      console.error(`Failed to ${isEditMode ? "update" : "create"} project group:`, err);
      setError(
        err?.response?.data?.message ||
          (isEditMode
            ? t("projects.detail.updateError") || "Не удалось обновить группу"
            : t("projects.detail.createError") || "Не удалось создать группу")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    skipDraftSaveRef.current = true;
    setName("");
    setError(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        {isEditMode
          ? t("projects.detail.editGroup") || "Редактировать группу"
          : t("projects.detail.newGroup") || "Новая группа"}
        <IconButton
          onClick={handleClose}
          sx={{ ml: "auto" }}
          disabled={loading}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("projects.detail.nameLabel") || "Название"}
            type="text"
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            error={!!error}
            helperText={error}
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ textTransform: "none" }}>
          {t("common.cancel") || "Отмена"}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !name.trim()}
          sx={{ textTransform: "none" }}
        >
          {loading
            ? t("common.saving") || "Сохранение..."
            : t("common.save") || "Сохранить"}
        </Button>
      </DialogActions>
      </Dialog>
      {!isEditMode && (
        <DraftPromptDialog
          open={draftPromptOpen}
          onUseDraft={handleUseDraft}
          onStartNew={handleStartNew}
        />
      )}
    </>
  );
};

export default ProjectGroupForm;